import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  IconButton,
  Tabs,
  Tab,
  Paper,
  Container,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Chip,
  Checkbox,
  Select,
  MenuItem,
  InputLabel,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  CircularProgress
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import LanguageIcon from '@mui/icons-material/Language';
import DescriptionIcon from '@mui/icons-material/Description';
import EditIcon from '@mui/icons-material/Edit';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import ImageIcon from '@mui/icons-material/Image';
import CodeIcon from '@mui/icons-material/Code';
import ArticleIcon from '@mui/icons-material/Article';
import TableChartIcon from '@mui/icons-material/TableChart';
import SaveIcon from '@mui/icons-material/Save';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import Cookies from '../../common/Cookies';
import LoadProgress from '../../common/LoadProgress.js';
import SystemAlert from '../../common/SystemAlert.js';
// ...removed GoogleDriveModal import

export default function KnowledgeInfo({ isEmbedded = false, onStepCompleted = null, onCancel = null, kbIdFromWizard = null }) {
  // Google Drive Picker logic
  const GOOGLE_CLIENT_ID = '785785065927-8c30qia8amqbp7i4b432di1qg588vr62.apps.googleusercontent.com';
  const GOOGLE_API_KEY = 'AIzaSyD47YvDfQadptwblV1680mJ07xnNnQ9oj8';
  const GOOGLE_PICKER_SCOPE = 'https://www.googleapis.com/auth/drive.readonly';
  const GOOGLE_PICKER_VIEW_ID = 'DOCS';
  const oauthToken = useRef(null);
  const pickerLoaded = useRef(false);
  const gisLoaded = useRef(false);
  const [gdriveConnected, setGdriveConnected] = useState(false);
  /** Tenants linked to a reseller should not see Google Drive connect UI. */
  const [showGoogleDriveIntegration, setShowGoogleDriveIntegration] = useState(false);

  useEffect(() => {
    (async () => {
      const jwt = Cookies.getJwtToken();
      let allowGdrive = false;
      if (jwt) {
        try {
          const decoded = jwtDecode(jwt);
          const tenantId = decoded.tenantId ?? decoded.tenant_id;
          if (tenantId == null) {
            allowGdrive = true;
          } else {
            try {
              const resellerResp = await axios.get(`/public/reseller/by-tenant/${tenantId}`);
              allowGdrive = !(resellerResp.data && resellerResp.data.id != null);
            } catch {
              // 404 or network: treat as not a reseller tenant; keep Drive available
              allowGdrive = true;
            }
          }
        } catch {
          allowGdrive = true;
        }
      }
      setShowGoogleDriveIntegration(allowGdrive);

      if (!allowGdrive) {
        return;
      }

      // Load GIS
      if (!gisLoaded.current) {
        const gisScript = document.createElement('script');
        gisScript.src = 'https://accounts.google.com/gsi/client';
        gisScript.onload = () => { gisLoaded.current = true; };
        document.body.appendChild(gisScript);
      }
      // Load Picker
      if (!pickerLoaded.current) {
        const pickerScript = document.createElement('script');
        pickerScript.src = 'https://apis.google.com/js/api.js';
        pickerScript.onload = () => {
          window.gapi.load('picker', () => {
            pickerLoaded.current = true;
          });
        };
        document.body.appendChild(pickerScript);
      }
      // Check Google Drive connection status on mount
      try {
        if (!jwt) return;
        const statusResp = await axios.get('/gdrive/status', { headers: { Authorization: `Bearer ${jwt}` } });
        setGdriveConnected(!!(statusResp.data && statusResp.data.connected));
      } catch (e) {
        setGdriveConnected(false);
      }
    })();
  }, []);

  // Helper: Send Google OAuth code to backend with JWT
  async function sendGoogleCodeToBackend(code) {
    const jwt = Cookies.getJwtToken();
    if (!jwt) {
      alert('User not authenticated.');
      return;
    }
    try {
      // Send explicit redirectUri so backend can use the exact flow the client used.
      await axios.post('/gdrive/callback',
        { code, redirectUri: 'postmessage' },
        { headers: { Authorization: `Bearer ${jwt}` } }
      );
      // After successful backend call, fetch access token
      const resp = await axios.get('/gdrive/accessToken', {
        headers: { Authorization: `Bearer ${jwt}` }
      });
      if (resp.data && resp.data.accessToken) {
        oauthToken.current = resp.data.accessToken;
        launchPicker();
      } else {
        alert('Failed to retrieve Google access token.');
      }
    } catch (err) {
      alert('Failed to connect Google Drive.');
    }
  }

  const handleGoogleDriveClick = () => {
    if (!gisLoaded.current || !pickerLoaded.current) {
      alert('Google APIs not loaded yet. Please wait.');
      return;
    }
    (async () => {
      try {
        const jwt = Cookies.getJwtToken();
        if (!jwt) {
          alert('User not authenticated.');
          return;
        }
        const statusResp = await axios.get('/gdrive/status', { headers: { Authorization: `Bearer ${jwt}` } });
        if (statusResp && statusResp.data && statusResp.data.connected) {
          setGdriveConnected(true);
          // Already connected (possibly refreshed). Fetch access token and open picker.
          try {
            const atResp = await axios.get('/gdrive/accessToken', { headers: { Authorization: `Bearer ${jwt}` } });
            if (atResp.data && atResp.data.accessToken) {
              oauthToken.current = atResp.data.accessToken;
              launchPicker();
              return;
            }
          } catch (e) {
            console.log('Failed to fetch access token despite connected status', e);
            // fallthrough to starting OAuth flow
          }
        } else {
          setGdriveConnected(false);
        }
      } catch (err) {
        setGdriveConnected(false);
        // status endpoint failed; continue to OAuth flow
        console.log('gdrive status check failed or not connected:', err);
      }
      // Not connected - start OAuth flow that exchanges code with backend
      window.google.accounts.oauth2.initCodeClient({
        client_id: GOOGLE_CLIENT_ID,
        scope: GOOGLE_PICKER_SCOPE,
        callback: (response) => {
          if (response && response.code) {
            // Send code to backend with JWT
            sendGoogleCodeToBackend(response.code);
            setGdriveConnected(true);
            // Optionally, you can also launch the picker after successful connection
            // oauthToken.current = ... (fetch access token if needed for picker)
          } else {
            alert('Failed to authenticate with Google.');
          }
        }
      }).requestCode();
    })();
  };

  const launchPicker = () => {
    // DocsView with folders enabled
    const docsView = new window.google.picker.DocsView()
      .setIncludeFolders(true)
      .setSelectFolderEnabled(true);

    const picker = new window.google.picker.PickerBuilder()
      .addView(docsView)
      .addView(window.google.picker.ViewId.FOLDERS)
      .setOAuthToken(oauthToken.current)
      .setDeveloperKey(GOOGLE_API_KEY)
      .enableFeature(window.google.picker.Feature.MULTISELECT_ENABLED)
      .setCallback(pickerCallback)
      .build();
    picker.setVisible(true);
  };

  const pickerCallback = (data) => {
    if (data.action === window.google.picker.Action.PICKED) {
      const files = data.docs.map(doc => ({
        id: doc.id,
        name: doc.name,
        mimeType: doc.mimeType,
        source: 'gdrive'
      }));
      setKnowledgeFiles(prev => [...prev, ...files]);
    }
  };
  const navigate = useNavigate();
  const { kbId } = useParams(); // 'userId' must match the parameter name in your Route path, e.g., <Route path="/users/:userId" element={<UserProfile />} />
  const [knowledgeBaseId, setKnowledgeBaseId] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState({});
  const [websiteUrl, setWebsiteUrl] = useState('https://');
  const [editingIndex, setEditingIndex] = useState(-1);
  const [urlError, setUrlError] = useState('');
  const [loading, setLoading] = useState(knowledgeBaseId ? true : false);
  const [alertState, setAlertState] = useState(null);
  const [alertOpen, setAlertOpen] = useState(false);

  // Form state
  const [kbName, setKbName] = useState('');
  const [knowledgeFiles, setKnowledgeFiles] = useState([]);
  const [websiteUrls, setWebsiteUrls] = useState([]);
  const [existingDocuments, setExistingDocuments] = useState([]);
  const [existingWebsites, setExistingWebsites] = useState([]);
  const [nameError, setNameError] = useState('');
  const kbNameRef = useRef(null);

  // Periodic scraping state
  const [scrapingSchedule, setScrapingSchedule] = useState({}); // { url: 'weekly' | 'monthly' | null }
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
  const [selectedUrl, setSelectedUrl] = useState('');
  const [selectedFrequency, setSelectedFrequency] = useState('none');

  // Scraping modes state - single source of truth
  const [scrapingModes, setScrapingModes] = useState({}); // { url: 'single_page' | 'whole_site' | 'subdirectory' | 'sitemap' }

  // Scraping mode state for new URL input
  const [scrapingMode, setScrapingMode] = useState('single_page'); // 'single_page', 'whole_site', 'subdirectory', 'sitemap'

  // Domain expansion state
  const [expandedDomains, setExpandedDomains] = useState({}); // { domain: true/false }

  // Sitemap state
  const [sitemapDialogOpen, setSitemapDialogOpen] = useState(false);
  const [sitemapLinks, setSitemapLinks] = useState([]);
  const [selectedSitemapLinks, setSelectedSitemapLinks] = useState([]);
  const [fetchingSitemap, setFetchingSitemap] = useState(false);

  // Whole site confirmation state
  const [wholeSiteConfirmDialogOpen, setWholeSiteConfirmDialogOpen] = useState(false);
  const [pendingWholeSiteUrl, setPendingWholeSiteUrl] = useState('');

  // Scraping progress tracking
  const [scrapingStatus, setScrapingStatus] = useState(null);
  const [scrapingProgressOpen, setScrapingProgressOpen] = useState(false);
  const [scrapingActive, setScrapingActive] = useState(false); // Track if scraping is active in background
  const scrapingPollIntervalRef = useRef(null);
  const scrapingCompletionHandledRef = useRef(false); // Track if completion has been handled to prevent repeated calls
  const wasMonitoringScrapingRef = useRef(false); // Track if we were actively monitoring scraping (vs just checking status on page load)

  // Focus the knowledge base name field when component mounts
  useEffect(() => {
    var kBaseId = null;
    if (!kbId && kbIdFromWizard)
      kBaseId = kbIdFromWizard;
    if (kbId)
      kBaseId = kbId;
    setKnowledgeBaseId(kBaseId);

    if (kBaseId)
      fetchKnowledgeBase(kBaseId);

    if (kbNameRef.current) {
      kbNameRef.current.focus();
    }
  }, []);

  const fetchKnowledgeBase = async (kBaseId) => {
    try {
      setLoading(true);
      const response = await axios.post('/kbs/getKbDetail', null, { params: { kbId: kBaseId } });
      console.log(response.data);
      const kbData = response.data;

      setKbName(kbData.kbName || '');
      setWebsiteUrls(kbData.websites || []);

      // Initialize scraping schedule from existing data
      if (kbData.scrapingSchedule) {
        setScrapingSchedule(kbData.scrapingSchedule);
      }

      // Initialize scraping modes from existing data
      if (kbData.scrapingModes) {
        setScrapingModes(kbData.scrapingModes);
      }

      // Fetch existing documents
      const documentsResponse = await axios.post('/kbs/getKbDocuments', null, { params: { kbId: kBaseId } });
      console.log('Existing documents:', documentsResponse.data);
      setExistingDocuments(documentsResponse.data || []);

      // Fetch existing websites with details
      const websitesResponse = await axios.post('/kbs/getKbWebsiteDetails', null, { params: { kbId: kBaseId } });
      console.log('Existing websites:', websitesResponse.data);
      setExistingWebsites(websitesResponse.data || []);

      // Check if scraping is still active for this KB (don't show completion message on page load)
      checkScrapingStatus(kBaseId, false);

      setLoading(false);
    } catch (error) {
      console.error('Error fetching knowledge base:', error);
      setAlertState({ severity: 'error', message: 'Failed to load knowledge base details' });
      setAlertOpen(true);
      setLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleFileUpload = (e) => {
    const allowedTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'text/csv',
      'image/jpeg',
      'image/png',
      'image/gif',
      'text/markdown'
    ];

    const markdownExtensions = ['md', 'markdown'];
    const newFiles = Array.from(e.target.files).filter(file => {
      const extension = file.name.split('.').pop().toLowerCase();
      return allowedTypes.includes(file.type) || markdownExtensions.includes(extension);
    });

    setKnowledgeFiles([...knowledgeFiles, ...newFiles]);
  };

  const getFileIcon = (fileName) => {
    const extension = fileName.split('.').pop().toLowerCase();

    switch (extension) {
      case 'pdf':
        return <PictureAsPdfIcon fontSize="small" sx={{ color: '#f44336', mr: 1 }} />;
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
      case 'svg':
        return <ImageIcon fontSize="small" sx={{ color: '#4caf50', mr: 1 }} />;
      case 'doc':
      case 'docx':
      case 'txt':
        return <ArticleIcon fontSize="small" sx={{ color: '#2196f3', mr: 1 }} />;
      case 'md':
      case 'markdown':
        return (
          <Box
            component="span"
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: 24,
              height: 20,
              px: 0.75,
              borderRadius: 1,
              backgroundColor: '#e3f2fd',
              color: '#1976d2',
              fontSize: '0.7rem',
              fontWeight: 700,
              lineHeight: 1,
              mr: 1
            }}
          >
            MD
          </Box>
        );
      case 'xls':
      case 'xlsx':
      case 'csv':
        return <TableChartIcon fontSize="small" sx={{ color: '#4CAF50', mr: 1 }} />;
      case 'js':
      case 'jsx':
      case 'ts':
      case 'tsx':
      case 'html':
      case 'css':
      case 'json':
        return <CodeIcon fontSize="small" sx={{ color: '#ff9800', mr: 1 }} />;
      default:
        return <InsertDriveFileIcon fontSize="small" sx={{ color: '#9e9e9e', mr: 1 }} />;
    }
  };

  const handleRemoveFile = (index) => {
    // Validation: Check if this is the last file and there are no websites or existing documents
    const totalDocumentsAfterRemoval = existingDocuments.length + knowledgeFiles.length - 1;
    const totalWebsites = existingWebsites.length + websiteUrls.length;
    if (totalDocumentsAfterRemoval === 0 && totalWebsites === 0) {
      setAlertState({
        severity: 'error',
        message: 'Cannot delete the last document. Please add at least one more document or website before removing the last document.'
      });
      setAlertOpen(true);
      setTimeout(() => setAlertOpen(false), 6000);
      return;
    }

    const updatedFiles = [...knowledgeFiles];
    updatedFiles.splice(index, 1);
    setKnowledgeFiles(updatedFiles);

    const newUploadStatus = { ...uploadStatus };
    delete newUploadStatus[index];
    setUploadStatus(newUploadStatus);
  };
  // Helper to split files by source
  const getFilesBySource = () => {
    const localFiles = knowledgeFiles.filter(f => !f.source);
    const gdriveFiles = knowledgeFiles.filter(f => f.source === 'gdrive');
    return { localFiles, gdriveFiles };
  };

  // Example upload handler (to be called when uploading knowledgeFiles)
  const handleUploadKnowledgeFiles = async () => {
    setUploading(true);
    const { localFiles, gdriveFiles } = getFilesBySource();
    // Upload local files as before
    for (let i = 0; i < localFiles.length; i++) {
      const file = localFiles[i];
      const formData = new FormData();
      formData.append('file', file);
      // ...add other form fields as needed
      try {
        setUploadStatus(prev => ({ ...prev, [i]: 'uploading' }));
        const response = await axios.post('/kbs/uploadDocument', formData);
        setUploadStatus(prev => ({ ...prev, [i]: 'success' }));
      } catch (err) {
        setUploadStatus(prev => ({ ...prev, [i]: 'error' }));
      }
    }
    // Upload Google Drive files by sending their IDs to backend
    if (gdriveFiles.length > 0) {
      try {
        // Send array of file IDs to backend
        const fileIds = gdriveFiles.map(f => f.id);
        const response = await axios.post('/api/gdrive/uploadDocuments', {
          kbId: knowledgeBaseId,
          fileIds
        });
        // Mark all gdrive files as success
        gdriveFiles.forEach((f, idx) => {
          setUploadStatus(prev => ({ ...prev, [localFiles.length + idx]: 'success' }));
        });
      } catch (err) {
        let errorMsg = 'Failed to upload Google Drive file.';
        if (err.response && err.response.data) {
          // Try to extract backend error message
          if (typeof err.response.data === 'string') {
            errorMsg = err.response.data;
          } else if (err.response.data.message) {
            errorMsg = err.response.data.message;
          }
        }
        gdriveFiles.forEach((f, idx) => {
          setUploadStatus(prev => ({ ...prev, [localFiles.length + idx]: 'error' }));
        });
        setAlertState({ severity: 'error', message: errorMsg });
        setAlertOpen(true);
      }
    }
    setUploading(false);
  };

  const handleRemoveExistingDocument = async (doc) => {
    try {
      // Validation: Check if this is the last document and there are no websites
      const totalWebsites = existingWebsites.length + websiteUrls.length;
      if (existingDocuments.length === 1 && totalWebsites === 0) {
        setAlertState({
          severity: 'error',
          message: 'Cannot delete the last document. Please add at least one more document or website before removing the last document.'
        });
        setAlertOpen(true);
        setTimeout(() => setAlertOpen(false), 6000);
        return;
      }

      setLoading(true);
      console.log('[KnowledgeInfo] delete document request', { kbId: knowledgeBaseId, fileId: doc.id, fileName: doc.fileName, documentSource: doc.documentSource });
      await axios.post('/kbs/deleteKbDocument', null, {
        params: {
          kbId: knowledgeBaseId,
          fileId: doc.id,
          fileName: doc.fileName,
          documentSource: doc.documentSource
        }
      });
      // Remove from local state using DB id (robust for google_drive or uploaded_file)
      setExistingDocuments(prev => prev.filter(d => d.id !== doc.id));
      console.log('[KnowledgeInfo] delete document response: success for id', doc.id);
      setAlertState({ severity: 'success', message: 'Document deleted successfully' });
      setAlertOpen(true);
      setTimeout(() => setAlertOpen(false), 3000);
    } catch (error) {
      console.error('Error deleting document:', error);
      setAlertState({ severity: 'error', message: 'Failed to delete document' });
      setAlertOpen(true);
      setTimeout(() => setAlertOpen(false), 6000);
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveExistingWebsite = async (siteUrl, wholeSite) => {
    console.log('=== DELETE OPERATION ===');
    console.log('Original siteUrl:', siteUrl);
    console.log('Whole site deletion:', wholeSite);

    // Validation: Check if this deletion would remove the last website(s) and there are no documents
    let websitesToBeDeletedFromExisting = 0;
    let websitesToBeDeletedFromNew = 0;

    if (wholeSite) {
      // For whole site deletion, count all websites from the same domain
      const domainToDelete = siteUrl;
      websitesToBeDeletedFromExisting = existingWebsites.filter(website => {
        const websiteDomain = extractDomain(website.siteUrl);
        return websiteDomain === domainToDelete;
      }).length;
      websitesToBeDeletedFromNew = websiteUrls.filter(url => {
        const urlDomain = extractDomain(url);
        return urlDomain === domainToDelete;
      }).length;
    } else {
      // For single URL deletion, count only this specific URL
      websitesToBeDeletedFromExisting = existingWebsites.filter(website => website.siteUrl === siteUrl).length;
      websitesToBeDeletedFromNew = websiteUrls.filter(url => url === siteUrl).length;
    }

    const totalWebsitesAfterDeletion = existingWebsites.length - websitesToBeDeletedFromExisting + websiteUrls.length - websitesToBeDeletedFromNew;
    const totalDocuments = existingDocuments.length + knowledgeFiles.length;

    if (totalWebsitesAfterDeletion === 0 && totalDocuments === 0) {
      setAlertState({
        severity: 'error',
        message: 'Cannot delete the last website. Please add at least one more document or website before removing the last website.'
      });
      setAlertOpen(true);
      setTimeout(() => setAlertOpen(false), 6000);
      return;
    }

    try {
      setLoading(true);
      await axios.post('/kbs/deleteKbWebsite', null, {
        params: {
          kbId: knowledgeBaseId,
          siteUrl: siteUrl,
          wholeSite: wholeSite ? true : false
        }
      });

      if (wholeSite) {
        // For whole website deletion - remove all URLs from the domain
        console.log('Whole website deletion - clearing all domain URLs');

        // Extract domain from the siteUrl
        console.log('Domain to clear:', siteUrl);

        // Remove all websites that belong to this domain
        setExistingWebsites(prev => {
          const filtered = prev.filter(website => {
            const websiteDomain = extractDomain(website.siteUrl);
            return websiteDomain !== siteUrl;
          });
          console.log('Websites before filtering:', prev.length);
          console.log('Websites after filtering:', filtered.length);
          return filtered;
        });

        // Remove all URLs from websiteUrls that belong to this domain
        setWebsiteUrls(prev => {
          const filtered = prev.filter(url => {
            const urlDomain = extractDomain(url);
            return urlDomain !== siteUrl;
          });
          console.log('WebsiteUrls before filtering:', prev.length);
          console.log('WebsiteUrls after filtering:', filtered.length);
          return filtered;
        });

        // Remove all scraping schedules for this domain
        setScrapingSchedule(prev => {
          const filtered = { ...prev };
          Object.keys(filtered).forEach(url => {
            const urlDomain = extractDomain(url);
            if (urlDomain === siteUrl) {
              delete filtered[url];
            }
          });
          console.log('Scraping schedule before filtering:', Object.keys(prev).length);
          console.log('Scraping schedule after filtering:', Object.keys(filtered).length);
          return filtered;
        });

        // Remove all scraping modes for this domain
        setScrapingModes(prev => {
          const filtered = { ...prev };
          Object.keys(filtered).forEach(url => {
            const urlDomain = extractDomain(url);
            if (urlDomain === siteUrl) {
              delete filtered[url];
            }
          });
          console.log('Scraping modes before filtering:', Object.keys(prev).length);
          console.log('Scraping modes after filtering:', Object.keys(filtered).length);
          return filtered;
        });

      } else {
        // For single URL deletion - remove only the specific URL
        console.log('Single URL deletion - removing specific URL');

        setExistingWebsites(prev => prev.filter(website => website.siteUrl !== siteUrl));
        setWebsiteUrls(prev => prev.filter(url => url !== siteUrl));

        // Remove from scraping schedule
        const newSchedule = { ...scrapingSchedule };
        delete newSchedule[siteUrl];
        setScrapingSchedule(newSchedule);

        // Remove from scraping modes
        const newScrapingModes = { ...scrapingModes };
        delete newScrapingModes[siteUrl];
        setScrapingModes(newScrapingModes);
      }

      setAlertState({ severity: 'success', message: 'Website and all its documents deleted successfully' });
      setAlertOpen(true);
      setTimeout(() => setAlertOpen(false), 3000);
    } catch (error) {
      console.error('Error deleting website:', error);
      setAlertState({ severity: 'error', message: 'Failed to delete website' });
      setAlertOpen(true);
      setTimeout(() => setAlertOpen(false), 6000);
    } finally {
      setLoading(false);
    }
  };

  // Periodic scraping functions
  const handleScheduleClick = (url) => {
    setSelectedUrl(url);
    setSelectedFrequency(scrapingSchedule[url] || 'none');
    setScheduleDialogOpen(true);
  };

  const handleScheduleSave = () => {
    if (selectedFrequency === 'none') {
      const newSchedule = { ...scrapingSchedule };
      delete newSchedule[selectedUrl];
      setScrapingSchedule(newSchedule);
    } else {
      setScrapingSchedule(prev => ({
        ...prev,
        [selectedUrl]: selectedFrequency
      }));
    }
    setScheduleDialogOpen(false);
  };

  const handleScheduleCancel = () => {
    setScheduleDialogOpen(false);
  };

  // Sitemap functions
  const fetchSitemapLinks = async () => {
    if (!websiteUrl) return;

    try {
      setFetchingSitemap(true);
      const response = await axios.post('/kbs/fetchSitemap', null, {
        params: {
          url: websiteUrl
        }
      });

      if (response.data.success) {
        setSitemapLinks(response.data.links || []);
        setSelectedSitemapLinks([]); // Reset selections
        setSitemapDialogOpen(true);
      } else {
        setAlertState({ severity: 'error', message: response.data.message || 'Failed to fetch sitemap links. Are you sure your site has a sitemap?' });
        setAlertOpen(true);
      }
    } catch (error) {
      console.error('Error fetching sitemap:', error);
      setAlertState({ severity: 'error', message: 'Failed to fetch sitemap links. Are you sure site has sitemap?' });
      setAlertOpen(true);
    } finally {
      setFetchingSitemap(false);
    }
  };

  const handleSitemapLinkToggle = (link) => {
    setSelectedSitemapLinks(prev => {
      if (prev.includes(link)) {
        return prev.filter(l => l !== link);
      } else {
        return [...prev, link];
      }
    });
  };

  const handleSelectAllSitemapLinks = () => {
    setSelectedSitemapLinks(sitemapLinks);
  };

  const handleDeselectAllSitemapLinks = () => {
    setSelectedSitemapLinks([]);
  };

  const handleAddSelectedSitemapLinks = () => {
    // Add selected links to websiteUrls with single_page scraping mode
    const newUrls = selectedSitemapLinks.filter(link => !websiteUrls.includes(link));

    setWebsiteUrls(prev => [...prev, ...newUrls]);

    // Set scraping mode for all selected links to single_page
    const newScrapingModes = { ...scrapingModes };
    selectedSitemapLinks.forEach(link => {
      newScrapingModes[link] = 'single_page';
    });
    setScrapingModes(newScrapingModes);

    // Close dialog and reset
    setSitemapDialogOpen(false);
    setSitemapLinks([]);
    setSelectedSitemapLinks([]);
    setWebsiteUrl('https://');
    setScrapingMode('single_page');

    setAlertState({ severity: 'success', message: `Added ${newUrls.length} URLs from sitemap` });
    setAlertOpen(true);
  };

  const handleSitemapDialogClose = () => {
    setSitemapDialogOpen(false);
    setSitemapLinks([]);
    setSelectedSitemapLinks([]);
  };

  // Whole site confirmation functions
  const handleWholeSiteConfirm = () => {
    // Add the pending URL to the table
    const urlToStore = pendingWholeSiteUrl;
    setWebsiteUrls(prev => [...prev, urlToStore]);

    // Set scraping mode for this URL
    setScrapingModes(prev => ({
      ...prev,
      [urlToStore]: 'whole_site'
    }));

    // Close dialog and reset
    setWholeSiteConfirmDialogOpen(false);
    setPendingWholeSiteUrl('');
    setWebsiteUrl('https://');
    setScrapingMode('single_page');

    setAlertState({ severity: 'success', message: 'Website added for whole site scraping (limited to 100 pages)' });
    setAlertOpen(true);
  };

  const handleWholeSiteConfirmCancel = () => {
    setWholeSiteConfirmDialogOpen(false);
    setPendingWholeSiteUrl('');
  };

  const handleWholeSiteSubmit = () => {
    if (!websiteUrl) return;

    try {
      const urlObj = new URL(websiteUrl);
      if (urlObj.protocol !== 'http:' && urlObj.protocol !== 'https:') {
        setUrlError('Please enter a valid URL with http:// or https://');
        return;
      }

      // Check for duplicates
      if (editingIndex === -1) {
        try {
          const urlObj = new URL(websiteUrl);
          const urlToCheck = `${urlObj.protocol}//${urlObj.hostname}`;

          if (websiteUrls.includes(urlToCheck)) {
            setUrlError('This domain has already been added');
            return;
          }
        } catch (e) {
          // Fallback to original URL if parsing fails
          if (websiteUrls.includes(websiteUrl)) {
            setUrlError('This URL has already been added');
            return;
          }
        }
      }

      // Store the pending URL and show confirmation dialog
      setPendingWholeSiteUrl(websiteUrl);
      setWholeSiteConfirmDialogOpen(true);

    } catch (e) {
      setUrlError('Please enter a valid URL (e.g., https://example.com)');
    }
  };

  const getScheduleChip = (url) => {
    const frequency = scrapingSchedule[url];
    if (!frequency) return null;

    return (
      <Chip
        label={frequency === 'weekly' ? 'Weekly' : 'Monthly'}
        size="small"
        color="primary"
        variant="outlined"
        sx={{ ml: 1, fontSize: '0.7rem' }}
      />
    );
  };

  // Helper function to extract domain from URL
  const extractDomain = (url) => {
    try {
      const urlObj = new URL(url);
      let hostname = urlObj.hostname;
      // Remove www. prefix if present
      if (hostname.startsWith('www.')) {
        hostname = hostname.substring(4);
      }
      return hostname;
    } catch (e) {
      return null;
    }
  };

  // Helper function to check if URL is domain-level (no path beyond root)
  const isDomainLevelUrl = (url) => {
    try {
      const urlObj = new URL(url);
      const pathname = urlObj.pathname;
      // Consider it domain-level if path is empty, "/" or just "/index.html"
      return pathname === '' || pathname === '/' || pathname === '/index.html';
    } catch (e) {
      return false;
    }
  };

  // Note: Domain conflict checking removed - no longer needed with dropdown selection

  // Note: Whole website dialog functions removed - now using single scraping mode dropdown

  const getScrapingModeChip = (url) => {
    const mode = scrapingModes[url];
    if (!mode || mode === 'single_page') return null;

    let label = "";
    let color = "secondary";

    switch (mode) {
      case 'whole_site':
        label = "Whole site";
        color = "secondary";
        break;
      case 'subdirectory':
        label = "Subdirectory only";
        color = "secondary";
        break;
      case 'sitemap':
        label = "Sitemap";
        color = "info";
        break;
      default:
        return null;
    }

    return (
      <Chip
        label={label}
        size="small"
        color={color}
        variant="outlined"
        sx={{ ml: 1, fontSize: '0.7rem' }}
      />
    );
  };

  // Domain expansion functions
  const handleDomainToggle = (domain) => {
    setExpandedDomains(prev => ({
      ...prev,
      [domain]: !prev[domain]
    }));
  };

  // Group websites by domain - only group if they have whole website scraping
  const groupWebsitesByDomain = (websites) => {
    const grouped = {};
    const singlePageWebsites = [];

    websites.forEach(website => {
      const domain = extractDomain(website.siteUrl);
      if (!domain) return;

      // Check if this website has whole website or subdirectory scraping enabled
      const mode = scrapingModes[website.siteUrl] || 'single_page';
      const hasWholeWebsiteScraping = mode === 'whole_site' || mode === 'subdirectory';

      if (hasWholeWebsiteScraping) {
        // Group whole website scraping URLs by domain
        if (!grouped[domain]) {
          grouped[domain] = {
            domain,
            websites: [],
            hasWholeWebsiteScraping: true,
            totalUrls: 0
          };
        }
        grouped[domain].websites.push(website);
        grouped[domain].totalUrls++;
      } else {
        // Keep single page URLs separate
        singlePageWebsites.push(website);
      }
    });

    // Convert single page websites to individual groups
    singlePageWebsites.forEach(website => {
      const domain = extractDomain(website.siteUrl);
      const key = `single-${website.id}`; // Use unique key for single pages
      grouped[key] = {
        domain: domain,
        websites: [website],
        hasWholeWebsiteScraping: false,
        totalUrls: 1,
        isSinglePage: true
      };
    });

    return grouped;
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return 'Unknown size';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown date';
    return new Date(dateString).toLocaleDateString();
  };

  const uploadFilesToServer = async () => {
    setUploading(true);

    try {
      const newUploadStatus = { ...uploadStatus };

      for (let i = 0; i < knowledgeFiles.length; i++) {
        if (uploadStatus[i] === 'success') continue;

        const file = knowledgeFiles[i];
        newUploadStatus[i] = 'uploading';
        setUploadStatus(newUploadStatus);

        const formData = new FormData();
        formData.append('file', file);

        try {
          const response = await axios.post('/kbs/uploadDocument', formData, {
            headers: {
              'Content-Type': 'multipart/form-data'
            },
            onUploadProgress: (progressEvent) => {
              const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
              console.log(`${file.name} upload progress: ${percentCompleted}%`);
            }
          });

          newUploadStatus[i] = 'success';
          console.log(`File ${file.name} uploaded successfully:`, response.data);
        } catch (error) {
          newUploadStatus[i] = 'error';
          console.error(`Error uploading file ${file.name}:`, error);
        }

        setUploadStatus({ ...newUploadStatus });
      }
    } finally {
      setUploading(false);
    }
  };

  const handleWebsiteSubmit = () => {
    if (!websiteUrl) return;

    try {
      const urlObj = new URL(websiteUrl);
      if (urlObj.protocol !== 'http:' && urlObj.protocol !== 'https:') {
        setUrlError('Please enter a valid URL with http:// or https://');
        return;
      }

      // Check for duplicates
      if (editingIndex === -1) {
        let urlToCheck = websiteUrl;
        if (scrapingMode === 'whole_site') {
          try {
            const urlObj = new URL(websiteUrl);
            urlToCheck = `${urlObj.protocol}//${urlObj.hostname}`;
          } catch (e) {
            urlToCheck = websiteUrl;
          }
        }

        if (websiteUrls.includes(urlToCheck)) {
          setUrlError(scrapingMode === 'whole_site' ? 'This domain has already been added' : 'This URL has already been added');
          return;
        }
      }

      // Note: Domain conflict checking removed - users can now freely choose scraping modes

      if (editingIndex >= 0) {
        const oldUrl = websiteUrls[editingIndex];

        // For whole site scraping, show just the base domain URL
        let urlToStore = websiteUrl;
        if (scrapingMode === 'whole_site') {
          try {
            const urlObj = new URL(websiteUrl);
            urlToStore = `${urlObj.protocol}//${urlObj.hostname}`;
          } catch (e) {
            // If URL parsing fails, keep original URL
            urlToStore = websiteUrl;
          }
        }

        const updatedUrls = [...websiteUrls];
        updatedUrls[editingIndex] = urlToStore;
        setWebsiteUrls(updatedUrls);

        // Update scraping schedule if URL changed
        if (oldUrl !== urlToStore) {
          const newSchedule = { ...scrapingSchedule };
          if (newSchedule[oldUrl]) {
            newSchedule[urlToStore] = newSchedule[oldUrl];
            delete newSchedule[oldUrl];
            setScrapingSchedule(newSchedule);
          }
        }

        // Update scraping modes - always update to current selected mode
        const newScrapingModes = { ...scrapingModes };

        if (oldUrl !== urlToStore) {
          // URL changed - move the old mode to new URL
          if (newScrapingModes[oldUrl] !== undefined) {
            newScrapingModes[urlToStore] = scrapingMode; // Use the new mode, not the old one
            delete newScrapingModes[oldUrl];
          }
        } else {
          // URL didn't change - update mode for same URL
          newScrapingModes[urlToStore] = scrapingMode;
        }

        setScrapingModes(newScrapingModes);

        setEditingIndex(-1);
      } else {
        // For whole site scraping, show just the base domain URL
        let urlToStore = websiteUrl;
        if (scrapingMode === 'whole_site') {
          try {
            const urlObj = new URL(websiteUrl);
            urlToStore = `${urlObj.protocol}//${urlObj.hostname}`;
          } catch (e) {
            // If URL parsing fails, keep original URL
            urlToStore = websiteUrl;
          }
        }

        setWebsiteUrls([...websiteUrls, urlToStore]);

        // Store the scraping mode for this URL
        setScrapingModes(prev => ({
          ...prev,
          [urlToStore]: scrapingMode
        }));
      }

      setWebsiteUrl('https://'); // <-- Always reset to https://
      setScrapingMode('single_page'); // Reset to default scraping mode
      setUrlError('');
    } catch (e) {
      setUrlError('Please enter a valid URL (e.g., https://example.com)');
    }
  };

  const handleEditWebsite = (index) => {
    const urlToEdit = websiteUrls[index];
    setWebsiteUrl(urlToEdit);
    setEditingIndex(index);

    // Also populate the scraping mode for editing
    const currentScrapingMode = scrapingModes[urlToEdit] || 'single_page';
    setScrapingMode(currentScrapingMode);

    // If the URL changes, we need to update the scraping schedule
    // This will be handled in handleWebsiteSubmit when the edit is confirmed
  };

  const handleEditWebsiteByUrl = (url) => {
    const index = websiteUrls.findIndex(u => u === url);
    if (index !== -1) {
      setWebsiteUrl(url);
      setEditingIndex(index);

      // Also populate the scraping mode for editing
      const currentScrapingMode = scrapingModes[url] || 'single_page';
      setScrapingMode(currentScrapingMode);
    }
  };

  const handleRemoveWebsite = (index) => {
    // Validation: Check if this is the last website and there are no documents
    const totalWebsitesAfterRemoval = existingWebsites.length + websiteUrls.length - 1;
    const totalDocuments = existingDocuments.length + knowledgeFiles.length;
    if (totalWebsitesAfterRemoval === 0 && totalDocuments === 0) {
      setAlertState({
        severity: 'error',
        message: 'Cannot delete the last website. Please add at least one more document or website before removing the last website.'
      });
      setAlertOpen(true);
      setTimeout(() => setAlertOpen(false), 6000);
      return;
    }

    const urlToRemove = websiteUrls[index];
    const updatedUrls = [...websiteUrls];
    updatedUrls.splice(index, 1);
    setWebsiteUrls(updatedUrls);

    // Remove from scraping schedule
    const newSchedule = { ...scrapingSchedule };
    delete newSchedule[urlToRemove];
    setScrapingSchedule(newSchedule);

    // Remove from scraping modes
    const newScrapingModes = { ...scrapingModes };
    delete newScrapingModes[urlToRemove];
    setScrapingModes(newScrapingModes);

    if (editingIndex === index) {
      setEditingIndex(-1);
      setWebsiteUrl('');
    } else if (editingIndex > index) {
      setEditingIndex(editingIndex - 1);
    }
  };

  const handleRemoveWebsiteByUrl = (url) => {
    const index = websiteUrls.findIndex(u => u === url);
    if (index !== -1) {
      handleRemoveWebsite(index);
    }
  };

  const checkKbExists = async (name) => {
    if (!name.trim()) {
      setNameError('');
      return;
    }

    try {
      const response = await axios.post('/kbs/kbExists', {
        kbName: name,
        id: knowledgeBaseId || null
      });

      if (response.data.exists) {
        setNameError('Knowledge base name already exists');
      } else {
        setNameError('');
      }
    } catch (error) {
      console.error('Error checking knowledge base name:', error);
    }
  };

  // Check scraping status (one-time check, doesn't start polling)
  // showCompletionMessage: only true if we were actively monitoring scraping
  const checkScrapingStatus = async (kbId, showCompletionMessage = false) => {
    try {
      const jwt = Cookies.getJwtToken();
      const response = await axios.get('/kbs/scrapingStatus', {
        params: { kbId },
        headers: { Authorization: `Bearer ${jwt}` }
      });

      if (response.data && response.data.exists) {
        if (response.data.status === 'running') {
          // Scraping is active
          setScrapingActive(true);
          setScrapingStatus(response.data);
          scrapingCompletionHandledRef.current = false; // Reset completion flag when running
          wasMonitoringScrapingRef.current = true; // Mark that we're monitoring active scraping (whether found on page load or during monitoring)
        } else if (response.data.status === 'completed' || response.data.status === 'failed') {
          // Completed or failed - update status but mark as inactive
          setScrapingActive(false);
          setScrapingStatus(response.data);

          // Only show completion message if we were actively monitoring AND haven't shown it yet
          if (showCompletionMessage && wasMonitoringScrapingRef.current && !scrapingCompletionHandledRef.current) {
            scrapingCompletionHandledRef.current = true;

            // Optionally show completion message (only if dialog is not open - fetchScrapingStatus will handle it if dialog is open)
            if (response.data.status === 'completed' && !scrapingProgressOpen) {
              setAlertState({
                severity: 'success',
                message: `Scraping completed! Scraped ${response.data.totalDocuments} documents from ${response.data.successfulUrls}/${response.data.totalUrls} URLs.`
              });
              setAlertOpen(true);
              // Refresh knowledge base to show new documents (only once)
              if (kbId) {
                fetchKnowledgeBase(kbId);
              }
            }
          }

          // Reset monitoring flag after handling completion
          if (response.data.status === 'completed' || response.data.status === 'failed') {
            wasMonitoringScrapingRef.current = false;
          }
        }
      } else {
        setScrapingActive(false);
        setScrapingStatus(null);
        scrapingCompletionHandledRef.current = false; // Reset when no status exists
        wasMonitoringScrapingRef.current = false; // Reset monitoring flag
      }
    } catch (error) {
      console.error('Error checking scraping status:', error);
      setScrapingActive(false);
    }
  };

  // Polling function for scraping status
  const startScrapingStatusPolling = (kbId) => {
    // Clear any existing polling
    if (scrapingPollIntervalRef.current) {
      clearInterval(scrapingPollIntervalRef.current);
    }

    // Initial fetch
    fetchScrapingStatus(kbId);

    // Poll every 2 seconds
    scrapingPollIntervalRef.current = setInterval(() => {
      fetchScrapingStatus(kbId);
    }, 2000);
  };

  // Resume polling and open progress dialog
  const reopenScrapingProgress = (kbId) => {
    if (kbId && scrapingActive) {
      setScrapingProgressOpen(true);
      startScrapingStatusPolling(kbId);
    }
  };

  const fetchScrapingStatus = async (kbId) => {
    try {
      const jwt = Cookies.getJwtToken();
      const response = await axios.get('/kbs/scrapingStatus', {
        params: { kbId },
        headers: { Authorization: `Bearer ${jwt}` }
      });

      if (response.data && response.data.exists) {
        setScrapingStatus(response.data);

        // Update scraping active state
        setScrapingActive(response.data.status === 'running');

        // If completed or failed, stop polling and close dialog after a delay
        if (response.data.status === 'completed' || response.data.status === 'failed') {
          setScrapingActive(false);

          // Stop polling immediately
          if (scrapingPollIntervalRef.current) {
            clearInterval(scrapingPollIntervalRef.current);
            scrapingPollIntervalRef.current = null;
          }

          // Only handle completion once to prevent repeated messages and reloads
          if (!scrapingCompletionHandledRef.current) {
            scrapingCompletionHandledRef.current = true;

            // Show completion message
            const message = response.data.status === 'completed'
              ? `Scraping completed! Scraped ${response.data.totalDocuments} documents from ${response.data.successfulUrls}/${response.data.totalUrls} URLs.`
              : `Scraping failed: ${response.data.message}`;

            setAlertState({
              severity: response.data.status === 'completed' ? 'success' : 'error',
              message: message
            });
            setAlertOpen(true);

            // Auto-close progress dialog after 3 seconds
            setTimeout(() => {
              setScrapingProgressOpen(false);
              // Refresh knowledge base to show new documents (only once)
              if (kbId) {
                fetchKnowledgeBase(kbId);
              }
            }, 3000);
          }
        }
      } else {
        // No status found - might have been cleaned up or never started
        setScrapingActive(false);
        if (scrapingPollIntervalRef.current) {
          clearInterval(scrapingPollIntervalRef.current);
          scrapingPollIntervalRef.current = null;
        }
        setScrapingProgressOpen(false);
      }
    } catch (error) {
      console.error('Error fetching scraping status:', error);
      // Continue polling even on error (might be temporary)
    }
  };

  // Periodic status check when scraping is active but dialog is closed (lightweight check every 5 seconds)
  useEffect(() => {
    if (!scrapingActive || !knowledgeBaseId || scrapingProgressOpen) {
      // Don't start interval if conditions aren't met
      return;
    }

    // Only do lightweight status check, don't poll aggressively
    const statusCheckInterval = setInterval(() => {
      // Show completion message if we're actively monitoring
      checkScrapingStatus(knowledgeBaseId, true);
    }, 5000); // Check every 5 seconds when banner is visible but dialog is closed

    return () => {
      clearInterval(statusCheckInterval);
    };
  }, [scrapingActive, knowledgeBaseId, scrapingProgressOpen]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (scrapingPollIntervalRef.current) {
        clearInterval(scrapingPollIntervalRef.current);
      }
    };
  }, []);

  const handleSave = async () => {
    if (!kbName) {
      setAlertState({ severity: 'error', message: 'Please enter a knowledge base name' });
      setAlertOpen(true);
      setTimeout(() => setAlertOpen(false), 6000);
      return;
    }

    if (nameError) {
      setAlertState({ severity: 'error', message: 'Please fix the knowledge base name error' });
      setAlertOpen(true);
      setTimeout(() => setAlertOpen(false), 6000);
      return;
    }

    // Filter out existing websites from websiteUrls to only send new websites
    const newWebsites = websiteUrls.filter(url => !existingWebsites.some(w => w.siteUrl === url));

    // Check if there are any new websites, files, or existing content
    if (newWebsites.length === 0 && knowledgeFiles.length === 0 && existingDocuments.length === 0 && existingWebsites.length === 0) {
      setAlertState({ severity: 'error', message: 'Please add at least one website or document' });
      setAlertOpen(true);
      setTimeout(() => setAlertOpen(false), 6000);
      return;
    }
    try {
      setLoading(true);
      const formData = new FormData();
      const kbData = {
        id: knowledgeBaseId || 0,
        kbName: kbName.trim(),
        websites: newWebsites, // New websites to be added and scraped
        existingWebsites: existingWebsites.map(w => w.siteUrl), // Existing websites (URLs only)
        scrapingSchedule: scrapingSchedule, // Include scraping schedule in the save
        scrapingModes: scrapingModes // Include scraping modes for each URL
      };
      console.log('kbData', kbData);
      formData.append('kbData', JSON.stringify(kbData));
      knowledgeFiles.forEach(file => {
        formData.append('documents', file);
      });
      // Add Google Drive file IDs to formData
      const googleFileIds = knowledgeFiles.filter(f => f.source === 'gdrive').map(f => f.id);
      if (googleFileIds.length > 0) {
        googleFileIds.forEach(id => formData.append('googleFileIds', id));
      }
      const response = await axios.post('/kbs/saveKb', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      // Robust failed-files handling: support backend returning names, ids, or objects
      // Collect failed files from multiple possible backend keys (failedFiles, googleDriveFailedFiles)
      let failedFiles = [];
      if (Array.isArray(response.data.failedFiles)) failedFiles = failedFiles.concat(response.data.failedFiles);
      if (Array.isArray(response.data.googleDriveFailedFiles)) failedFiles = failedFiles.concat(response.data.googleDriveFailedFiles);
      if (failedFiles.length > 0) {
        // Normalize labels for display. If backend returned an ID, try to resolve to the
        // original file name from the current `knowledgeFiles` state for a friendlier message.
        const failedLabels = failedFiles.map(ff => {
          if (!ff) return 'unknown';
          if (typeof ff === 'string') {
            // Try to resolve a known file name by id
            const matched = knowledgeFiles.find(f => f.id === ff || f.name === ff);
            return (matched && matched.name) ? matched.name : ff;
          }
          return ff.name || ff.fileName || ff.id || JSON.stringify(ff);
        });

        // Remove failed files from knowledgeFiles by matching id or name
        setKnowledgeFiles(prev => prev.filter(file => {
          return !failedFiles.some(ff => {
            if (!ff) return false;
            if (typeof ff === 'string') {
              return (file.name && file.name === ff) || (file.id && file.id === ff);
            } else {
              return (ff.id && file.id && ff.id === file.id) || (ff.name && file.name && ff.name === file.name);
            }
          });
        }));

        let errorMsg = `Some files could not be processed: ${failedLabels.join(', ')}.`;
        // Avoid appending the generic 'Knowledge base updated/created successfully' message
        // which is set earlier by the backend and is misleading when some files failed.
        const backendMsg = response.data.message || '';
        if (backendMsg && !/knowledge base (created|updated) successfully/i.test(backendMsg)) {
          errorMsg += ` ${backendMsg}`;
        } else {
          errorMsg += ' This file type is not supported. Please upload files with selectable text or supported formats such as PDF, DOCX, XLSX, CSV, TXT, Google Docs, or Google Sheets.';
        }
        setAlertState({ severity: 'error', message: errorMsg, persistent: true });
        setAlertOpen(true);
        // Keep the error visible longer (15s) so users can read details
        setTimeout(() => setAlertOpen(false), 15000);
        setLoading(false);
        return; // Prevent showing success alert if there are failed files
      } else if (response.data.success) {
        // Website scraping error logic (updated)
        // Check for synchronous scraping results (if scraping completed before response)
        if (
          response.data.scrapeResults &&
          Array.isArray(response.data.scrapeResults.results)
        ) {
          const failedUrls = response.data.scrapeResults.results
            .filter(r => r.success === false && r.url)
            .map(r => r.url);
          if (failedUrls.length > 0) {
            setWebsiteUrls(prev => prev.filter(url => !failedUrls.includes(url)));
            setAlertState({ severity: 'success', message: response.data.message || 'Knowledge base saved successfully' });
            setAlertOpen(true);
            setTimeout(() => {
              setAlertState({
                severity: 'error',
                message: `Some websites could not be scraped: ${failedUrls.join(', ')}. Please check the URLs and try again.`,
                persistent: true
              });
              setAlertOpen(true);
            }, 3500);
            setLoading(false);
            return;
          }
        }

        // Handle asynchronous scraping (scraping started in background)
        if (response.data.scrapingStarted && response.data.kbId) {
          // Reset completion flag for new scraping operation
          scrapingCompletionHandledRef.current = false;
          wasMonitoringScrapingRef.current = true; // Mark that we're now monitoring active scraping
          // Start polling for scraping status
          startScrapingStatusPolling(response.data.kbId);
          // Show progress dialog
          setScrapingProgressOpen(true);
          setAlertState({
            severity: 'info',
            message: 'Knowledge base saved successfully. Website scraping has started.'
          });
          setAlertOpen(true);
        }
        // Fallback: If backend message indicates 0 chunks, treat as error
        if (
          typeof response.data.message === 'string' &&
          (/0\s*chunks|no text could be extracted|no text found/i.test(response.data.message))
        ) {
          setAlertState({
            severity: 'error',
            message: response.data.message || 'No text could be extracted from the uploaded files. Please upload files with selectable text.'
          });
          setAlertOpen(true);
        } else {
          // Only show success alert if scraping hasn't started (to avoid duplicate alerts)
          // If scrapingStarted is true, we already showed an info alert above
          if (!response.data.scrapingStarted) {
            setAlertState({ severity: 'success', message: response.data.message || 'Knowledge base saved successfully' });
            setAlertOpen(true);
          }
        }

        // Update knowledgeBaseId state if we got a new ID from the backend
        if (response.data.kbId) {
          setKnowledgeBaseId(response.data.kbId);
        }

        // Handle navigation and cleanup regardless of scraping status
        if (isEmbedded && onStepCompleted) {
          // When embedded in AgentCreation, call the callback to mark step as completed
          onStepCompleted(response.data.kbId);
        } else if (response.data.kbId && !isEmbedded) {
          // When not embedded, navigate to the knowledge base detail page
          navigate(`/KnowledgeInfo/${response.data.kbId}`, { replace: true });
        }

        // After navigation or callback, refresh and clear new files/websites
        if (response.data.kbId) {
          await fetchKnowledgeBase(response.data.kbId);

          // Clear new files and websites that have been processed
          setKnowledgeFiles([]);
          setUploadStatus({});
          setWebsiteUrls(prev =>
            prev.filter(url => !existingWebsites.some(w => w.siteUrl === url))
          );

          // Clear scraping modes for processed websites
          setScrapingModes(prev => {
            const filtered = { ...prev };
            Object.keys(filtered).forEach(url => {
              if (existingWebsites.some(w => w.siteUrl === url)) {
                delete filtered[url];
              }
            });
            return filtered;
          });
        }

        if (response.data.warning) {
          setTimeout(() => {
            setAlertState({ severity: 'warning', message: response.data.warning });
            setAlertOpen(true);
          }, 3000);
        }
      } else {
        setAlertState({ severity: 'error', message: response.data.message || 'Failed to save knowledge base' });
        setAlertOpen(true);
      }
    } catch (error) {
      console.error('Error saving knowledge base:', error);
      if (error.response) {
        const errorMessage = error.response.data?.message || `Server error: ${error.response.status}`;
        setAlertState({ severity: 'error', message: errorMessage });
        setAlertOpen(true);
      } else if (error.request) {
        setAlertState({ severity: 'error', message: 'Network error: Unable to reach server' });
        setAlertOpen(true);
      } else {
        setAlertState({ severity: 'error', message: 'Failed to save knowledge base: ' + error.message });
        setAlertOpen(true);
      }
    } finally {
      setLoading(false); // <-- This ensures the loading dialog always closes
    }
  };

  return (
    <div>
      {alertState && (
        <SystemAlert open={alertOpen} severity={alertState.severity} message={alertState.message} onClose={() => setAlertOpen(false)} />
      )}

      {loading && <LoadProgress open={loading} />}

      {!isEmbedded &&
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
          <Typography variant="h5" component="h1">
            {knowledgeBaseId ? 'Edit Knowledge Base' : 'Create Knowledge Base'}
          </Typography>
          <Box>
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/KnowledgeList')}
              sx={{ mr: 2 }}
            >
              Back to List
            </Button>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleSave}
            >
              Save Knowledge Base
            </Button>
          </Box>
        </Box>
      }

      <Paper elevation={0} sx={{ p: 3 }}>
        {/* Scraping Active Banner - Only show if scraping is actually running */}
        {scrapingActive && knowledgeBaseId && scrapingStatus && scrapingStatus.status === 'running' && (
          <Box
            sx={{
              mb: 2,
              p: 2,
              backgroundColor: 'info.light',
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'primary.main',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 1
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
              <CircularProgress size={20} sx={{ color: 'common.white' }} />
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 'medium', color: 'common.white' }}>
                  Website scraping is running in the background
                  {scrapingStatus && (
                    <span style={{ marginLeft: '8px', color: 'rgba(255, 255, 255, 0.9)' }}>
                      ({scrapingStatus.completedUrls} / {scrapingStatus.totalUrls} URLs completed)
                    </span>
                  )}
                </Typography>
                {scrapingStatus && scrapingStatus.currentUrl && scrapingStatus.urlStatuses && scrapingStatus.urlStatuses[scrapingStatus.currentUrl] && (
                  (() => {
                    const currentUrlStatus = scrapingStatus.urlStatuses[scrapingStatus.currentUrl];
                    if (currentUrlStatus.totalPages > 0) {
                      return (
                        <Typography variant="caption" sx={{ mt: 0.5, display: 'block', color: 'rgba(255, 255, 255, 0.9)' }}>
                          Processing page {currentUrlStatus.processedPages} / {currentUrlStatus.totalPages} for current URL
                        </Typography>
                      );
                    }
                    return null;
                  })()
                )}
              </Box>
            </Box>
            <Button
              variant="contained"
              size="small"
              onClick={() => reopenScrapingProgress(knowledgeBaseId)}
              sx={{ ml: 'auto' }}
            >
              View Progress
            </Button>
          </Box>
        )}

        <Box sx={{ mt: 1, minHeight: '300px' }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {/* Add knowledge to your knowledge base from websites or document uploads. Chattitude currently accepts the following file types: Microsoft Word and Excel, PDF's, and TXT. */}
            You will train your agent on your knowledge here.  This includes uploading documents, spreadsheets, and web pages so your agent can learn about your products, services, processes, and policies.
          </Typography>

          <Box sx={{ maxWidth: 400, width: '100%' }}>
            <TextField
              key={`kbName-${kbName}`}
              fullWidth
              label="Knowledge Base Name"
              variant="outlined"
              value={kbName}
              inputRef={kbNameRef}
              autoFocus
              onChange={(e) => {
                setKbName(e.target.value);
                setNameError('');
              }}
              onBlur={(e) => checkKbExists(e.target.value)}
              margin="normal"
              sx={{ mb: 3 }}
              placeholder="Enter a name for this knowledge base"
              required
              error={!!nameError}
              helperText={nameError}
            />
          </Box>

          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
            <Tabs value={tabValue} onChange={handleTabChange} aria-label="knowledge source tabs">
              <Tab icon={<LanguageIcon />} iconPosition="start" label="Website" />
              <Tab icon={<DescriptionIcon />} iconPosition="start" label="Documents" />
            </Tabs>
          </Box>

          {tabValue === 0 && (
            <Box>
              <Typography variant="body2" sx={{ mb: 2 }}>
                Enter website URLs to crawl and extract knowledge. Choose the scraping mode that best fits your needs.
              </Typography>


              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
                <TextField
                  label="Enter a link to your knowledge base"
                  placeholder="https://example.com"
                  variant="outlined"
                  value={websiteUrl}
                  onChange={(e) => {
                    setWebsiteUrl(e.target.value);
                    setUrlError('');
                  }}
                  onPaste={e => {
                    let paste = (e.clipboardData || window.clipboardData).getData('text');
                    if (/^https?:\/\//i.test(paste)) {
                      e.preventDefault();
                      setWebsiteUrl(paste);
                    }
                  }}
                  error={!!urlError}
                  helperText={urlError}
                  sx={{ flexGrow: 1, minWidth: '300px' }}
                />

                <Tooltip
                  title={
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>Scraping Modes:</Typography>
                      <Typography variant="caption" display="block">• Single page: Scrape only the specific URL</Typography>
                      <Typography variant="caption" display="block">• Whole site: Crawl the entire website</Typography>
                      <Typography variant="caption" display="block">• Subdirectory only: Scrape pages within the same directory</Typography>
                      <Typography variant="caption" display="block">• Sitemap: Use website sitemap for discovery</Typography>
                    </Box>
                  }
                  arrow
                  placement="top"
                >
                  <FormControl sx={{ minWidth: 200 }}>
                    <InputLabel>Scraping Mode</InputLabel>
                    <Select
                      value={scrapingMode}
                      label="Scraping Mode"
                      onChange={(e) => setScrapingMode(e.target.value)}
                    >
                      <MenuItem value="single_page">Single page</MenuItem>
                      <MenuItem value="whole_site">Whole site</MenuItem>
                      <MenuItem value="subdirectory">Subdirectory only</MenuItem>
                      <MenuItem value="sitemap">Sitemap</MenuItem>
                    </Select>
                  </FormControl>
                </Tooltip>

                <Button
                  variant="contained"
                  onClick={
                    scrapingMode === 'sitemap' ? fetchSitemapLinks :
                      scrapingMode === 'whole_site' ? handleWholeSiteSubmit :
                        handleWebsiteSubmit
                  }
                  disabled={!websiteUrl || fetchingSitemap}
                  sx={{ height: '56px', minWidth: '100px' }}
                >
                  {fetchingSitemap ? 'Fetching...' :
                    editingIndex >= 0 ? 'Update' :
                      scrapingMode === 'sitemap' ? 'Fetch' : 'Add'}
                </Button>
              </Box>

              {/* New Websites Section */}
              {websiteUrls.filter(url => !existingWebsites.some(w => w.siteUrl === url)).length > 0 && (
                <Box sx={{ mt: 2, mb: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="subtitle2">
                      New Websites to Add ({websiteUrls.filter(url => !existingWebsites.some(w => w.siteUrl === url)).length})
                    </Typography>
                    <Typography variant="caption" color="primary" sx={{ fontStyle: 'italic' }}>
                      Please Click "Save Knowledge Base" Button to scrape sites.
                    </Typography>
                  </Box>
                  <Card variant="outlined">
                    <CardContent sx={{ p: 1, "&:last-child": { pb: 1 } }}>
                      {websiteUrls.filter(url => !existingWebsites.some(w => w.siteUrl === url)).map((url, index) => (
                        <Box
                          key={index}
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            mb: 1,
                            p: 1,
                            backgroundColor: index % 2 === 0 ? 'transparent' : 'rgba(0, 0, 0, 0.04)'
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Typography variant="body2" sx={{ width: '30px', flexShrink: 0 }}>
                              {index + 1}.
                            </Typography>
                            <Tooltip title={url} arrow>
                              <Typography variant="body2" sx={{ wordBreak: 'break-all', maxWidth: '300px' }} noWrap>
                                {url}
                              </Typography>
                            </Tooltip>
                            {getScrapingModeChip(url)}
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Tooltip title="Set Scraping Schedule" arrow>
                              <IconButton size="small" onClick={() => handleScheduleClick(url)}>
                                <CalendarTodayIcon fontSize="small" sx={{ color: 'primary.main' }} />
                              </IconButton>
                            </Tooltip>
                            {getScheduleChip(url)}
                            <Tooltip title="Edit Website URL" arrow>
                              <IconButton size="small" onClick={() => handleEditWebsiteByUrl(url)}>
                                <EditIcon fontSize="small" sx={{ color: 'success.main' }} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Remove website" arrow>
                              <IconButton size="small" onClick={() => handleRemoveWebsiteByUrl(url)}>
                                <DeleteIcon fontSize="small" sx={{ color: 'error.main' }} />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </Box>
                      ))}
                    </CardContent>
                  </Card>
                </Box>
              )}

              {/* Existing Websites Section */}
              {existingWebsites.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Existing Webpages ({existingWebsites.length})
                  </Typography>
                  <Card variant="outlined">
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ backgroundColor: 'rgba(0, 0, 0, 0.08)' }}>
                          <th style={{ padding: '8px 16px', textAlign: 'left', width: '40px' }}>#</th>
                          <th style={{ padding: '8px 16px', textAlign: 'left' }}>Website Domain</th>
                          <th style={{ padding: '8px 16px', textAlign: 'left', width: '120px' }}>Scraped Date</th>
                          <th style={{ padding: '8px 16px', textAlign: 'center', width: '100px' }}>Whole Site</th>
                          <th style={{ padding: '8px 16px', textAlign: 'center', width: '120px' }}>Schedule</th>
                          <th style={{ padding: '8px 16px', textAlign: 'center', width: '80px' }}>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Object.values(groupWebsitesByDomain(existingWebsites)).map((domainGroup, domainIndex) => {
                          const isExpanded = expandedDomains[domainGroup.domain];
                          const representativeWebsite = domainGroup.websites[0]; // Use first website as representative

                          return (
                            <React.Fragment key={domainGroup.isSinglePage ? `single-${representativeWebsite.id}` : domainGroup.domain}>
                              {/* Domain Row */}
                              <tr
                                style={{
                                  backgroundColor: domainIndex % 2 === 0 ? 'transparent' : 'rgba(0, 0, 0, 0.04)'
                                }}
                              >
                                <td style={{ padding: '8px 16px', textAlign: 'center' }}>
                                  {domainIndex + 1}
                                </td>
                                <td style={{ padding: '8px 16px', textAlign: 'left' }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <LanguageIcon fontSize="small" sx={{ color: '#2196f3', mr: 1 }} />
                                    <Box sx={{ flexGrow: 1 }}>
                                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                        {domainGroup.isSinglePage ? representativeWebsite.siteUrl :
                                          (domainGroup.hasWholeWebsiteScraping ? domainGroup.domain : domainGroup.websites[0].siteUrl)}
                                      </Typography>
                                      {domainGroup.hasWholeWebsiteScraping && domainGroup.totalUrls > 1 && (
                                        <Typography variant="caption" color="text.secondary">
                                          {domainGroup.totalUrls} URLs
                                        </Typography>
                                      )}
                                    </Box>
                                    {domainGroup.hasWholeWebsiteScraping && domainGroup.totalUrls > 1 && (
                                      <IconButton
                                        size="small"
                                        onClick={() => handleDomainToggle(domainGroup.domain)}
                                        title={isExpanded ? "Collapse URLs" : "Expand to show all URLs"}
                                      >
                                        {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                      </IconButton>
                                    )}
                                  </Box>
                                </td>
                                <td style={{ padding: '8px 16px' }}>
                                  <Typography variant="caption" color="text.secondary">
                                    {formatDate(representativeWebsite.createdAt)}
                                  </Typography>
                                </td>
                                <td style={{ padding: '8px 16px', textAlign: 'center' }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    {(() => {
                                      const representativeUrl = representativeWebsite.siteUrl;
                                      const mode = scrapingModes[representativeUrl] || 'single_page';

                                      let label = "";
                                      let color = "primary";

                                      switch (mode) {
                                        case 'whole_site':
                                          label = "Whole site";
                                          color = "secondary";
                                          break;
                                        case 'subdirectory':
                                          label = "Subdirectory only";
                                          color = "secondary";
                                          break;
                                        case 'sitemap':
                                          label = "Sitemap";
                                          color = "info";
                                          break;
                                        default:
                                          label = "Single page";
                                          color = "primary";
                                      }

                                      return (
                                        <Chip
                                          label={label}
                                          size="small"
                                          color={color}
                                          variant="outlined"
                                          sx={{ fontSize: '0.7rem' }}
                                        />
                                      );
                                    })()}
                                  </Box>
                                </td>
                                <td style={{ padding: '8px 16px', textAlign: 'center' }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <Tooltip title="Set Scraping Schedule" arrow>
                                      <IconButton
                                        size="small"
                                        onClick={() => handleScheduleClick(representativeWebsite.siteUrl)}
                                        sx={{ mr: 1 }}
                                      >
                                        <CalendarTodayIcon fontSize="small" sx={{ color: 'primary.main' }} />
                                      </IconButton>
                                    </Tooltip>
                                    {getScheduleChip(representativeWebsite.siteUrl)}
                                  </Box>
                                </td>
                                <td style={{ padding: '8px 16px', textAlign: 'center' }}>
                                  <Tooltip title={domainGroup.hasWholeWebsiteScraping ? "Delete whole website" : "Delete website and all its documents"} arrow>
                                    <IconButton
                                      size="small"
                                      onClick={() => handleRemoveExistingWebsite(
                                        domainGroup.hasWholeWebsiteScraping ? domainGroup.domain : representativeWebsite.siteUrl, domainGroup.hasWholeWebsiteScraping
                                      )}
                                    >
                                      <DeleteIcon fontSize="small" sx={{ color: 'error.main' }} />
                                    </IconButton>
                                  </Tooltip>
                                </td>
                              </tr>

                              {/* Expanded URLs Rows */}
                              {isExpanded && domainGroup.hasWholeWebsiteScraping && (
                                <>
                                  {domainGroup.websites.map((website, urlIndex) => (
                                    <tr
                                      key={`${domainGroup.domain}-${website.id}`}
                                      style={{
                                        backgroundColor: 'rgba(0, 0, 0, 0.02)',
                                        borderLeft: '3px solid #2196f3'
                                      }}
                                    >
                                      <td style={{ padding: '8px 16px', textAlign: 'center' }}>
                                        <Typography variant="caption" color="text.secondary">
                                          {urlIndex + 1}
                                        </Typography>
                                      </td>
                                      <td style={{ padding: '8px 16px', paddingLeft: '32px', textAlign: 'left' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                          <LanguageIcon fontSize="small" sx={{ color: '#2196f3', mr: 1 }} />
                                          <Box>
                                            <Tooltip title={website.siteUrl} arrow>
                                              <Typography variant="body2" noWrap sx={{ maxWidth: '300px' }}>
                                                {website.siteUrl}
                                              </Typography>
                                            </Tooltip>
                                          </Box>
                                        </Box>
                                      </td>
                                      <td style={{ padding: '8px 16px' }}>
                                        <Typography variant="caption" color="text.secondary">
                                          -
                                        </Typography>
                                      </td>
                                      <td style={{ padding: '8px 16px', textAlign: 'center' }}>
                                        <Typography variant="caption" color="text.secondary">
                                          -
                                        </Typography>
                                      </td>
                                      <td style={{ padding: '8px 16px', textAlign: 'center' }}>
                                        <Typography variant="caption" color="text.secondary">
                                          -
                                        </Typography>
                                      </td>
                                      <td style={{ padding: '8px 16px', textAlign: 'center' }}>
                                        <Tooltip title="Delete website and all its documents" arrow>
                                          <IconButton
                                            size="small"
                                            onClick={() => handleRemoveExistingWebsite(website.siteUrl)}
                                          >
                                            <DeleteIcon fontSize="small" sx={{ color: 'error.main' }} />
                                          </IconButton>
                                        </Tooltip>
                                      </td>
                                    </tr>
                                  ))}
                                </>
                              )}
                            </React.Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                  </Card>
                </Box>
              )}


            </Box>
          )}

          {tabValue === 1 && (
            <Box>
              <Button
                variant="outlined"
                component="label"
                startIcon={<CloudUploadIcon />}
                sx={{ mb: 2, mr: 2 }}
              >
                Select Files
                <input
                  type="file"
                  hidden
                  multiple
                  accept=".pdf,.xlsx,.csv,.docx,.doc,.md,.markdown,text/markdown"
                  onChange={handleFileUpload}
                />
              </Button>
              {showGoogleDriveIntegration && (
                <>
                  <Button
                    variant={gdriveConnected ? 'contained' : 'outlined'}
                    color={gdriveConnected ? 'success' : 'primary'}
                    startIcon={<InsertDriveFileIcon />}
                    sx={{ mb: 2, mr: 1 }}
                    onClick={handleGoogleDriveClick}
                  >
                    {gdriveConnected ? 'Choose Your Google Docs' : 'Connect Google Drive'}
                  </Button>
                  {gdriveConnected && (
                    <Button
                      variant="outlined"
                      color="error"
                      sx={{ mb: 2, mr: 2 }}
                      onClick={async () => {
                        try {
                          const jwt = Cookies.getJwtToken();
                          if (!jwt) {
                            setAlertState({ severity: 'error', message: 'User not authenticated.' });
                            setAlertOpen(true);
                            return;
                          }
                          await axios.post('/gdrive/disconnect', {}, { headers: { Authorization: `Bearer ${jwt}` } });
                          setGdriveConnected(false);
                          setAlertState({ severity: 'success', message: 'Google Drive disconnected.' });
                          setAlertOpen(true);
                        } catch (err) {
                          setAlertState({ severity: 'error', message: 'Failed to disconnect Google Drive.' });
                          setAlertOpen(true);
                        }
                      }}
                    >
                      Disconnect Google Drive
                    </Button>
                  )}
                </>
              )}
              {/* Google Drive Modal */}
              {/* GoogleDriveModal removed. Picker is now launched directly from button. */}

              {/* Existing Documents Section */}
              {existingDocuments.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Existing Documents ({existingDocuments.length})
                  </Typography>
                  <Card variant="outlined">
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ backgroundColor: 'rgba(0, 0, 0, 0.08)' }}>
                          <th style={{ padding: '8px 16px', textAlign: 'left', width: '40px' }}>#</th>
                          <th style={{ padding: '8px 16px', textAlign: 'left' }}>Document</th>
                          <th style={{ padding: '8px 16px', textAlign: 'left', width: '120px' }}>Upload Date</th>
                          <th style={{ padding: '8px 16px', textAlign: 'center', width: '80px' }}>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {existingDocuments.map((doc, index) => (
                          <tr
                            key={doc.id}
                            style={{
                              backgroundColor: index % 2 === 0 ? 'transparent' : 'rgba(0, 0, 0, 0.04)'
                            }}
                          >
                            <td style={{ padding: '8px 16px', textAlign: 'center' }}>
                              {index + 1}
                            </td>
                            <td style={{ padding: '8px 16px' }}>
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                {/* Show Google Drive icon and file type icon if documentSource is 'google_drive' */}
                                {doc.documentSource === 'google_drive' && (
                                  <Tooltip title="Google Drive document" arrow>
                                    <img
                                      src="https://www.gstatic.com/images/branding/product/1x/drive_2020q4_48dp.png"
                                      alt="Google Drive"
                                      style={{ width: 20, height: 20, marginRight: 4, verticalAlign: 'middle' }}
                                    />
                                  </Tooltip>
                                )}
                                {/* Show file type icon (prefer fileType if available, fallback to fileName), with tooltip and alignment */}
                                {(() => {
                                  // Prefer doc.fileType if available (Word, Excel, PDF, CSV, etc.)
                                  const type = doc.fileType ? doc.fileType.toLowerCase() : '';
                                  const iconStyle = { width: 20, height: 20, marginRight: 4, verticalAlign: 'middle', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' };
                                  if (type === 'pdf') return (
                                    <Tooltip title="PDF" arrow>
                                      <span style={iconStyle}><PictureAsPdfIcon fontSize="small" sx={{ color: '#f44336' }} /></span>
                                    </Tooltip>
                                  );
                                  if (type === 'word') return (
                                    <Tooltip title="Word" arrow>
                                      <span style={iconStyle}><ArticleIcon fontSize="small" sx={{ color: '#2196f3' }} /></span>
                                    </Tooltip>
                                  );
                                  if (type === 'markdown') return (
                                    <Tooltip title="Markdown" arrow>
                                      <span style={iconStyle}>
                                        <Box
                                          component="span"
                                          sx={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            minWidth: 24,
                                            height: 20,
                                            px: 0.75,
                                            borderRadius: 1,
                                            backgroundColor: '#e3f2fd',
                                            color: '#1976d2',
                                            fontSize: '0.7rem',
                                            fontWeight: 700,
                                            lineHeight: 1
                                          }}
                                        >
                                          MD
                                        </Box>
                                      </span>
                                    </Tooltip>
                                  );
                                  if (type === 'excel') return (
                                    <Tooltip title="Excel" arrow>
                                      <span style={iconStyle}><TableChartIcon fontSize="small" sx={{ color: '#4CAF50' }} /></span>
                                    </Tooltip>
                                  );
                                  if (type === 'csv') return (
                                    <Tooltip title="CSV" arrow>
                                      <span style={iconStyle}><TableChartIcon fontSize="small" sx={{ color: '#4CAF50' }} /></span>
                                    </Tooltip>
                                  );
                                  // fallback to fileName extension with tooltip and alignment
                                  const ext = doc.fileName.split('.').pop().toLowerCase();
                                  let label = '';
                                  if (["pdf"].includes(ext)) label = "PDF";
                                  else if (["doc", "docx", "txt"].includes(ext)) label = "Word";
                                  else if (["md", "markdown"].includes(ext)) label = "Markdown";
                                  else if (["xls", "xlsx"].includes(ext)) label = "Excel";
                                  else if (["csv"].includes(ext)) label = "CSV";
                                  else if (["jpg", "jpeg", "png", "gif", "svg"].includes(ext)) label = "Image";
                                  else if (["js", "jsx", "ts", "tsx", "html", "css", "json"].includes(ext)) label = "Code";
                                  else label = "File";
                                  return (
                                    <Tooltip title={label} arrow>
                                      <span style={iconStyle}>{getFileIcon(doc.fileName)}</span>
                                    </Tooltip>
                                  );
                                })()}
                                <Box>
                                  <Typography variant="body2" noWrap sx={{ maxWidth: '300px' }}>
                                    {doc.fileName}
                                  </Typography>
                                </Box>
                              </Box>
                            </td>
                            <td style={{ padding: '8px 16px' }}>
                              <Typography variant="caption" color="text.secondary">
                                {formatDate(doc.createdAt)}
                              </Typography>
                            </td>
                            <td style={{ padding: '8px 16px', textAlign: 'center' }}>
                              <IconButton
                                size="small"
                                onClick={() => handleRemoveExistingDocument(doc)}
                                title="Delete document"
                              >
                                <DeleteIcon fontSize="small" sx={{ color: 'error.main' }} />
                              </IconButton>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </Card>
                </Box>
              )}

              {/* New Files Section */}
              {knowledgeFiles.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="subtitle2">
                      New Files to Upload ({knowledgeFiles.length})
                    </Typography>
                    <Typography variant="caption" color="primary" sx={{ fontStyle: 'italic' }}>
                      Please Click "Save Knowledge Base" Button to start uploading documents.
                    </Typography>
                  </Box>
                  <Card variant="outlined">
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <tbody>
                        {knowledgeFiles.map((file, index) => (
                          <tr
                            key={index}
                            style={{
                              backgroundColor: index % 2 === 0 ? 'transparent' : 'rgba(0, 0, 0, 0.04)'
                            }}
                          >
                            <td style={{ padding: '8px 16px', width: '40px', textAlign: 'center' }}>
                              {index + 1}
                            </td>
                            <td style={{ padding: '8px 16px' }}>
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                {getFileIcon(file.name)}
                                <Typography variant="body2" noWrap sx={{ maxWidth: '100%' }}>
                                  {file.name}
                                </Typography>
                              </Box>
                            </td>
                            <td style={{ padding: '8px 16px' }}>
                              <Typography variant="caption" color={
                                uploadStatus[index] === 'success' ? 'success.main' :
                                  uploadStatus[index] === 'error' ? 'error.main' :
                                    uploadStatus[index] === 'uploading' ? 'info.main' : 'text.secondary'
                              }>
                                {uploadStatus[index] === 'success' ? 'Uploaded' :
                                  uploadStatus[index] === 'error' ? 'Failed' :
                                    uploadStatus[index] === 'uploading' ? 'Uploading...' : 'Ready to upload'}
                              </Typography>
                            </td>
                            <td style={{ padding: '8px 16px', textAlign: 'center' }}>
                              <IconButton size="small" onClick={() => handleRemoveFile(index)}>
                                <DeleteIcon fontSize="small" sx={{ color: 'error.main' }} />
                              </IconButton>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </Card>
                </Box>
              )}
            </Box>
          )}
        </Box>

        {isEmbedded &&
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3, mt: 2 }}>
            <Typography variant="h5" component="h1">
              &nbsp;
            </Typography>
            <Box>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSave}
              >
                Save Knowledge Base
              </Button>
              <Button
                variant="outlined"
                onClick={onCancel}
                sx={{ ml: 2 }}
              >
                Cancel
              </Button>
            </Box>
          </Box>
        }

      </Paper>

      {/* Periodic Scraping Schedule Dialog */}
      <Dialog open={scheduleDialogOpen} onClose={handleScheduleCancel} maxWidth="sm" fullWidth>
        <DialogTitle>
          Set Periodic Scraping Schedule
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Select how often this website should be automatically scraped for updates.
            </Typography>
            <Typography variant="body2" sx={{ mb: 2, fontWeight: 'bold' }}>
              Website: {selectedUrl}
            </Typography>
            <FormControl component="fieldset" sx={{ width: '100%' }}>
              <FormLabel component="legend">Scraping Frequency</FormLabel>
              <RadioGroup
                value={selectedFrequency}
                onChange={(e) => setSelectedFrequency(e.target.value)}
              >
                <FormControlLabel
                  value="none"
                  control={<Radio />}
                  label="No periodic scraping (default)"
                />
                <FormControlLabel
                  value="weekly"
                  control={<Radio />}
                  label="Weekly - Scrape every week"
                />
                <FormControlLabel
                  value="monthly"
                  control={<Radio />}
                  label="Monthly - Scrape every month"
                />
              </RadioGroup>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleScheduleCancel} color="inherit">
            Cancel
          </Button>
          <Button onClick={handleScheduleSave} variant="contained" color="primary">
            Save Schedule
          </Button>
        </DialogActions>
      </Dialog>

      {/* Scraping Progress Dialog */}
      <Dialog open={scrapingProgressOpen} onClose={() => { }} maxWidth="sm" fullWidth>
        <DialogTitle>
          Website Scraping Progress
          {scrapingStatus && scrapingStatus.status === 'running' && (
            <CircularProgress size={20} sx={{ ml: 2 }} />
          )}
        </DialogTitle>
        <DialogContent>
          {scrapingStatus && scrapingStatus.exists ? (
            <Box>
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Progress
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {scrapingStatus.completedUrls} / {scrapingStatus.totalUrls} URLs
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={scrapingStatus.progress || 0}
                  sx={{ height: 8, borderRadius: 4 }}
                />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  {Math.round(scrapingStatus.progress || 0)}% complete
                </Typography>
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Status:
                  <Chip
                    label={scrapingStatus.status === 'running' ? 'In Progress' : scrapingStatus.status === 'completed' ? 'Completed' : 'Failed'}
                    color={scrapingStatus.status === 'running' ? 'primary' : scrapingStatus.status === 'completed' ? 'success' : 'error'}
                    size="small"
                    sx={{ ml: 1 }}
                  />
                </Typography>
                {scrapingStatus.currentUrl && (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      Currently scraping: <strong>{scrapingStatus.currentUrl}</strong>
                    </Typography>
                    {scrapingStatus.urlStatuses && scrapingStatus.urlStatuses[scrapingStatus.currentUrl] && (
                      (() => {
                        const currentUrlStatus = scrapingStatus.urlStatuses[scrapingStatus.currentUrl];
                        if (currentUrlStatus.totalPages > 0) {
                          const pageProgress = (currentUrlStatus.processedPages / currentUrlStatus.totalPages) * 100;
                          return (
                            <Box sx={{ mt: 1, p: 1.5, backgroundColor: 'action.hover', borderRadius: 1 }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                <Typography variant="body2" color="text.secondary">
                                  Page Progress
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 'medium' }}>
                                  {currentUrlStatus.processedPages} / {currentUrlStatus.totalPages} pages
                                </Typography>
                              </Box>
                              <LinearProgress
                                variant="determinate"
                                value={pageProgress}
                                sx={{ height: 6, borderRadius: 3 }}
                              />
                              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                                {Math.round(pageProgress)}% of pages processed
                              </Typography>
                            </Box>
                          );
                        }
                        return null;
                      })()
                    )}
                  </Box>
                )}
                {scrapingStatus.message && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {scrapingStatus.message}
                  </Typography>
                )}
              </Box>

              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Summary
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Typography variant="body2">
                    <strong>Total URLs:</strong> {scrapingStatus.totalUrls}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Completed:</strong> {scrapingStatus.completedUrls}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Successful:</strong> {scrapingStatus.successfulUrls}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Documents:</strong> {scrapingStatus.totalDocuments}
                  </Typography>
                </Box>
              </Box>

              {scrapingStatus.urlStatuses && Object.keys(scrapingStatus.urlStatuses).length > 0 && (
                <Box sx={{ mt: 3, maxHeight: '200px', overflow: 'auto' }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    URL Details
                  </Typography>
                  <List dense>
                    {Object.entries(scrapingStatus.urlStatuses).map(([url, urlStatus]) => (
                      <ListItem key={url} sx={{ flexDirection: 'column', alignItems: 'stretch', py: 1.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', mb: 0.5 }}>
                          <Typography variant="body2" sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {url}
                          </Typography>
                          <Chip
                            label={urlStatus.status}
                            size="small"
                            color={
                              urlStatus.status === 'completed' ? 'success' :
                                urlStatus.status === 'failed' ? 'error' :
                                  urlStatus.status === 'scraping' ? 'primary' : 'default'
                            }
                          />
                          {urlStatus.documentsCount > 0 && (
                            <Typography variant="caption" color="text.secondary">
                              {urlStatus.documentsCount} docs
                            </Typography>
                          )}
                        </Box>
                        {urlStatus.totalPages > 0 && (
                          <Box sx={{ mt: 0.5, mb: 0.5 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                              <Typography variant="caption" color="text.secondary">
                                Pages: {urlStatus.processedPages} / {urlStatus.totalPages}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {Math.round((urlStatus.processedPages / urlStatus.totalPages) * 100)}%
                              </Typography>
                            </Box>
                            <LinearProgress
                              variant="determinate"
                              value={(urlStatus.processedPages / urlStatus.totalPages) * 100}
                              sx={{ height: 4, borderRadius: 2 }}
                            />
                          </Box>
                        )}
                        {urlStatus.message && (
                          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                            {urlStatus.message}
                          </Typography>
                        )}
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}
            </Box>
          ) : (
            <Box sx={{ textAlign: 'center', py: 3 }}>
              <CircularProgress />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Starting scraping operation...
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {scrapingStatus && (scrapingStatus.status === 'completed' || scrapingStatus.status === 'failed') ? (
            <Button onClick={() => setScrapingProgressOpen(false)} variant="contained">
              Close
            </Button>
          ) : (
            <Button onClick={() => {
              // Allow closing but keep polling in background (actually stop polling to save resources)
              setScrapingProgressOpen(false);
              // Note: scrapingActive remains true, so user can reopen
            }} variant="outlined">
              Close (Continue in Background)
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Sitemap Links Selection Dialog */}
      <Dialog open={sitemapDialogOpen} onClose={handleSitemapDialogClose} maxWidth="md" fullWidth>
        <DialogTitle>
          Select URLs from Sitemap
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Found {sitemapLinks.length} URLs in the sitemap. Select the ones you want to scrape.
            </Typography>

            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <Button size="small" onClick={handleSelectAllSitemapLinks}>
                Select All
              </Button>
              <Button size="small" onClick={handleDeselectAllSitemapLinks}>
                Deselect All
              </Button>
              <Typography variant="body2" sx={{ ml: 'auto', alignSelf: 'center' }}>
                {selectedSitemapLinks.length} of {sitemapLinks.length} selected
              </Typography>
            </Box>

            <Box sx={{ maxHeight: '400px', overflow: 'auto', border: '1px solid #e0e0e0', borderRadius: 1 }}>
              {sitemapLinks.map((link, index) => (
                <Box
                  key={index}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    p: 1,
                    borderBottom: index < sitemapLinks.length - 1 ? '1px solid #f0f0f0' : 'none',
                    backgroundColor: selectedSitemapLinks.includes(link) ? 'rgba(25, 118, 210, 0.08)' : 'transparent',
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.04)'
                    }
                  }}
                >
                  <Checkbox
                    checked={selectedSitemapLinks.includes(link)}
                    onChange={() => handleSitemapLinkToggle(link)}
                    size="small"
                  />
                  <Typography variant="body2" sx={{ ml: 1, wordBreak: 'break-all', flexGrow: 1 }}>
                    {link}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleSitemapDialogClose} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleAddSelectedSitemapLinks}
            variant="contained"
            color="primary"
            disabled={selectedSitemapLinks.length === 0}
          >
            Add Selected URLs ({selectedSitemapLinks.length})
          </Button>
        </DialogActions>
      </Dialog>

      {/* Whole Site Confirmation Dialog */}
      <Dialog open={wholeSiteConfirmDialogOpen} onClose={handleWholeSiteConfirmCancel} maxWidth="sm" fullWidth>
        <DialogTitle>
          Confirm Whole Site Scraping
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body1" sx={{ mb: 2, fontWeight: 'bold' }}>
              You are about to add entire website to scraping list:
            </Typography>
            <Typography variant="body2" color="primary" sx={{ mb: 3, wordBreak: 'break-all' }}>
              {pendingWholeSiteUrl}
            </Typography>

            <Box sx={{
              backgroundColor: 'rgba(255, 152, 0, 0.1)',
              border: '1px solid rgba(255, 152, 0, 0.3)',
              borderRadius: 1,
              p: 2,
              mb: 2
            }}>
              <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#f57c00', mb: 1 }}>
                ⚠️ Important Notice
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                • The system will scrape up to <strong>100 pages</strong> from this website by default
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                • This includes the main page and discovered internal links
              </Typography>
              <Typography variant="body2">
                • Large websites may take several minutes to process
              </Typography>
            </Box>

            <Typography variant="body2" color="text.secondary">
              Click "OK" to proceed with adding whole site to scraping list, or "Cancel" to go back.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleWholeSiteConfirmCancel} color="inherit">
            Cancel
          </Button>
          <Button onClick={handleWholeSiteConfirm} variant="contained" color="primary">
            OK - Add to the list
          </Button>
        </DialogActions>
      </Dialog>

      {/* Whole Website Scraping Dialog removed - now using dropdown selection */}
    </div>
  );
}